import JSZip from 'jszip';
import { z } from 'zod';
import { paraSlug, slugsUnicos, usuarioPadraoDoSlug } from '@nyroh/contracts';
import { ClientData, ClientEntity } from '../types';
import { urlPublicaDoSite } from '../lib/site-publico';

// O "Leão de Chácara" - Validação estrita dos dados de entrada
const ClientDataSchema = z.object({
  id: z.string().catch(() => Date.now().toString(36) + Math.random().toString(36).substring(2)),
  campaignId: z.string().catch(''),
  targetId: z.string().catch(''),
  name: z.string().catch('Cliente Sem Nome'),
  address: z.string().catch('Endereço não informado'),
  rating: z.coerce.number().catch(5),
  reviewCount: z.coerce.number().catch(0),
  website: z.string().nullable().catch(null),
  categories: z.array(z.string()).catch([]),
  topReviews: z.array(z.any()).catch([]),
  summary: z.string().catch('Resumo não disponível.'),
  blueprint: z.array(z.any()).catch([]),
  placeUri: z.string().catch(''),
  phoneNumber: z.string().catch(''),
  openingHours: z.any().transform((val) => {
    if (typeof val === 'string') return val;
    if (typeof val === 'object' && val !== null) return JSON.stringify(val);
    return '';
  }).catch(''),
  businessStatus: z.string().catch('Operacional'),
  priceLevel: z.string().nullable().catch(null),
  amenities: z.array(z.string()).catch([]),
  photoAnalysis: z.string().catch(''),
  seoKeywords: z.array(z.string()).catch([]),
  createdAt: z.string().catch(() => new Date().toISOString())
});

export const parseClientZip = async (file: File): Promise<ClientEntity[]> => {
  const zip = new JSZip();
  let loadedZip;
  
  try {
    loadedZip = await zip.loadAsync(file);
  } catch (err) {
    console.error("Failed to load zip file", err);
    throw new Error("Falha ao ler o arquivo ZIP. Verifique se é um arquivo válido.");
  }

  const clients: ClientEntity[] = [];
  const processPromises: Promise<void>[] = [];

  loadedZip.forEach((relativePath, zipEntry) => {
    // Check for json files. Based on logs, they are named 'lead_data.json'.
    // We also keep 'data.json' support just in case.
    if (!zipEntry.dir && (zipEntry.name.endsWith('lead_data.json') || zipEntry.name.endsWith('data.json'))) {
      processPromises.push((async () => {
        try {
          // 1. Determine folder prefix
          const fileName = zipEntry.name.split('/').pop() || '';
          const folderPrefix = relativePath.substring(0, relativePath.length - fileName.length);
          
          // 2. Parse JSON
          const jsonContent = await zipEntry.async('string');
          let rawData: any; 
          
          try {
            rawData = JSON.parse(jsonContent);
          } catch (e) {
            console.warn(`Invalid JSON structure in ${relativePath}`);
            return;
          }

          // --- LOGIC UPDATE FOR NESTED STRUCTURE (Ditho OS Format) ---
          let sourceData: any = rawData;
          let embeddedHtml: string | null = null;

          // Case 1: Nested Ditho OS Structure
          // { client_data: { leadData: { ... }, siteContent: "<html>..." } }
          if (rawData.client_data && rawData.client_data.leadData) {
            sourceData = rawData.client_data.leadData;
            
            // Extract HTML from JSON if present
            if (rawData.client_data.siteContent) {
              embeddedHtml = rawData.client_data.siteContent;
            }
          } 
          // Case 2: Direct Lead Data wrapper
          // { leadData: { ... } }
          else if (rawData.leadData) {
             sourceData = rawData.leadData;
          }
          // Case 3: Flat structure (Old format) - sourceData remains rawData

          // 3. Validation & Safeguards
          let clientName = sourceData?.name;
          
          if (!clientName || typeof clientName !== 'string') {
            console.warn(`Missing 'name' in ${relativePath}, attempting fallback.`);
            // Try to guess name from folder structure
            const pathParts = folderPrefix.split('/');
            // Get the last non-empty part
            const folderName = pathParts.filter(p => p && p.trim() !== '').pop();
            clientName = folderName || "Cliente Sem Nome";
          }

          // Safe Data Construction using Zod
          const safeData: ClientData = ClientDataSchema.parse({
            ...sourceData,
            name: clientName,
            createdAt: new Date().toISOString() // FORCE NEW DATE ON UPLOAD (Fix for "Today" filter)
          });

          // 4. Find HTML
          // Priority 1: HTML embedded in JSON (siteContent)
          // Priority 2: index.html file in the ZIP folder
          let htmlContent = '';

          if (embeddedHtml) {
             htmlContent = embeddedHtml;
          } else {
             const htmlFile = loadedZip.file(folderPrefix + 'index.html');
             
             if (htmlFile) {
               htmlContent = await htmlFile.async('string');
             } else {
                // Placeholder if absolutely nothing found
                htmlContent = `
                   <!DOCTYPE html>
                   <html lang="pt-BR">
                   <head>
                     <meta charset="UTF-8">
                     <meta name="viewport" content="width=device-width, initial-scale=1.0">
                     <title>${clientName}</title>
                     <style>
                       body { font-family: system-ui, -apple-system, sans-serif; display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100vh; margin: 0; background: #f8fafc; color: #334155; }
                       .card { background: white; padding: 2rem; border-radius: 1rem; box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1); text-align: center; max-width: 400px; }
                       h1 { font-size: 1.5rem; margin-bottom: 1rem; color: #0f172a; }
                       p { line-height: 1.5; }
                     </style>
                   </head>
                   <body>
                     <div class="card">
                       <h1>${clientName}</h1>
                       <p>O conteúdo do site não foi encontrado no JSON nem como index.html.</p>
                     </div>
                   </body>
                   </html>
                `;
             }
          }

          // 5. Inject Robust Link Handler (Fixes WhatsApp, Tel, Mailto & External Links in Iframe)
          // This ensures consistent behavior for future exports/uploads
          
          // PREPARE STATIC WA LINK FOR EXPORTED FILES
          const rawPhone = (safeData.phoneNumber || '').replace(/[^0-9]/g, '');
          const staticWaLink = rawPhone ? `https://wa.me/55${rawPhone}?text=Ol%C3%A1!%20Vim%20pelo%20site.` : '#';

          const scriptInjection = `
            <script>
              document.addEventListener('DOMContentLoaded', function() {
                var clientWa = "${staticWaLink}";
                
                document.body.addEventListener('click', function(e) {
                  var target = e.target.closest('a');
                  if (target) {
                    var href = target.getAttribute('href');
                    
                    // 1. Prevent Dead Links
                    if (!href || href === '#' || href === 'javascript:void(0)' || href === '') {
                        e.preventDefault();
                        e.stopPropagation();
                        if (clientWa && clientWa !== '#') {
                            window.open(clientWa, '_blank');
                        }
                        return;
                    }

                    if (href && (
                        href.startsWith('tel:') ||
                        href.startsWith('mailto:') ||
                        href.startsWith('http') || 
                        href.startsWith('//') || 
                        href.includes('wa.me') || 
                        href.includes('whatsapp') ||
                        href.includes('instagram') ||
                        href.includes('facebook')
                    )) {
                       e.preventDefault(); 
                       e.stopPropagation();
                       window.open(href, '_blank');
                    }
                  }
                }, true);
              });
            </script>
          `;
          
          // Insert script before closing body tag
          if (htmlContent.includes('</body>')) {
            htmlContent = htmlContent.replace('</body>', scriptInjection + '</body>');
          } else {
            htmlContent += scriptInjection;
          }

          /*
           * 6. Slug pela regra do CONTRATO.
           *
           * A daqui não tinha LIMITE DE TAMANHO. `slugSchema` aceita no máximo
           * 120 caracteres, e o cliente HTTP valida o corpo inteiro antes de
           * enviar: um único projeto de nome comprido derrubava a importação dos
           * outros 134, com o despejo cru do zod como mensagem.
           *
           * `paraSlug` corta em 100 e nunca termina em hífen. A unicidade dentro
           * do lote é resolvida depois, sobre a lista inteira — aqui não dá para
           * saber o que os outros projetos geraram.
           */
          const baseSlug = paraSlug(clientName) || `cliente-${Date.now()}`;

          /*
           * O usuario vem do contrato (`usuarioPadraoDoSlug`), nao de uma regra
           * local. A daqui era `.replace(/-/g,'').substring(0, 15)` — divergia
           * da que o servidor grava, e todo slug com mais de 15 caracteres
           * produzia um usuario que o login nunca casaria.
           *
           * O PIN saiu: era `Math.floor(1000 + Math.random() * 9000)`, gerador
           * nao criptografico rodando no NAVEGADOR e gravado em texto puro. Quem
           * emite credencial e o servidor, com argon2id e senha padrao marcada
           * para troca obrigatoria.
           */
          safeData.username = usuarioPadraoDoSlug(baseSlug);

          clients.push({
            id: safeData.id,
            data: safeData,
            htmlContent,
            slug: baseSlug,
            status: 'pending'
          });

        } catch (err) {
          console.error(`Error processing ${relativePath}:`, err);
        }
      })());
    }
  });

  await Promise.all(processPromises);

  /*
    Unicidade do slug DENTRO do lote.

    Truncar em 100 caracteres faz dois nomes compridos e parecidos colidirem. A
    importação usa `ON CONFLICT DO NOTHING`, então a colisão não daria erro: ela
    sumiria com o segundo projeto, contado como "ignorado porque já existe". Um
    projeto perdido em silêncio é pior que um slug com sufixo.

    Feito aqui, sobre a lista inteira, porque durante o laço nenhum item sabe o
    que os outros geraram.
  */
  const unicos = slugsUnicos(clients.map((c) => c.slug));
  return clients.map((c, i) => ({ ...c, slug: unicos[i] }));
};

// --- EXPORT FUNCTION FOR CPANEL ---
export const exportClientForCPanel = async (client: ClientEntity): Promise<void> => {
  const zip = new JSZip();
  let finalHtml = client.htmlContent;
  
  const imagesFolder = zip.folder("assets/images");
  if (!imagesFolder) return;

  let imageCounter = 1;
  const urlMap = new Map<string, string>(); // Original URL -> local path

  // 1. Handle base64 images
  const base64Regex = /data:image\/(jpeg|png|webp|gif|svg\+xml);base64,([^"'\s)]+)/g;
  let match;
  while ((match = base64Regex.exec(client.htmlContent)) !== null) {
    const fullMatch = match[0];
    if (urlMap.has(fullMatch)) continue;

    const ext = match[1] === 'svg+xml' ? 'svg' : match[1];
    const base64Data = match[2];
    const filename = `img_${imageCounter++}.${ext}`;
    const localPath = `./assets/images/${filename}`;
    
    imagesFolder.file(filename, base64Data, { base64: true });
    urlMap.set(fullMatch, localPath);
  }

  // 2. Handle standard HTTP / HTTPS URLs or // URLs
  // This looks for src="..." and url(...)
  const urlRegex = /(?:src=["']|url\(['"]?)((?:https?:)?\/\/[^"')\s]+)(?:["']|\)['"]?)/g;
  while ((match = urlRegex.exec(client.htmlContent)) !== null) {
    const originalUrl = match[1];
    if (urlMap.has(originalUrl)) continue;

    // Convert // to https://
    const fetchUrl = originalUrl.startsWith('//') ? `https:${originalUrl}` : originalUrl;
    
    try {
      // Trying to fetch the image to bundle it
      const response = await fetch(fetchUrl);
      if (response.ok) {
        const blob = await response.blob();
        
        // Extract extension from URL, fallback to jpg
        let ext = 'jpg';
        const urlParts = fetchUrl.split('?')[0].split('.');
        if (urlParts.length > 1) {
          const possibleExt = urlParts.pop()?.toLowerCase();
          if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'avif'].includes(possibleExt || '')) {
            ext = possibleExt!;
          }
        }
        
        const filename = `img_${imageCounter++}.${ext}`;
        const localPath = `./assets/images/${filename}`;
        
        imagesFolder.file(filename, blob);
        urlMap.set(originalUrl, localPath);
      }
    } catch (err) {
      console.warn("Could not download image, maintaining original URL:", originalUrl);
      // If we can't download it (e.g. CORS), we just leave the original URL
      // But we will fix the // to https://
      if (originalUrl.startsWith('//')) {
         urlMap.set(originalUrl, fetchUrl);
      }
    }
  }

  // Replace all matched URLs in HTML
  for (const [original, replacement] of urlMap.entries()) {
    // Escape for regex, or just use split.join for safety
    finalHtml = finalHtml.split(original).join(replacement);
  }

  // Add the index.html at root
  zip.file("index.html", finalHtml);

  // Generate the zip blob
  const content = await zip.generateAsync({ type: "blob" });

  // Trigger download via DOM
  const url = window.URL.createObjectURL(content);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${client.slug}-cpanel-export.zip`;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  
  // Cleanup
  setTimeout(() => {
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }, 100);
};

// --- EXPORT FUNCTION ---
export const exportClientsToZip = async (clients: ClientEntity[], coverImages: Record<string, string> = {}): Promise<void> => {
  const zip = new JSZip();
  const origin = window.location.origin;

  clients.forEach(client => {
    try {
      const safeSlug = client.slug || `cliente-${client.id || Date.now()}`;
      // Create a folder for each client
      const folder = zip.folder(safeSlug);
      
      if (folder) {
        // Enrich data with system links
        const enrichedData = {
          ...client.data,
          dithoSitesMetadata: {
            // `publicUrl` e o endereco onde o site responde de verdade — o
            // subdominio servido pela borda, nao o `#/site/` do painel, que
            // exige sessao de operador e devolve 401 para quem receber o ZIP.
            publicUrl: urlPublicaDoSite(client),
            // `editUrl` continua no painel: editar E uma tela daqui.
            editUrl: `${origin}/#/site/${safeSlug}/edit`,
            exportedAt: new Date().toISOString()
          }
        };

        // Add data.json
        folder.file("data.json", JSON.stringify(enrichedData, null, 2));

        let finalHtml = client.htmlContent || '';
        
        // HANDLE PREVIEW IMAGE FOR WHATSAPP
        const coverDataUrl = coverImages[client.id];
        if (coverDataUrl && finalHtml) {
          // 1. Add image file to zip (remove data prefix)
          const base64Data = coverDataUrl.split(',')[1];
          if (base64Data) {
            folder.file("preview.jpg", base64Data, { base64: true });
          }

          // 2. Inject Open Graph Meta Tags into HTML
          // This ensures WhatsApp shows the image when the link is shared
          const metaTags = `
      <!-- Social Media Preview Tags (Generated by Ditho Sites) -->
      <meta property="og:title" content="${client.data?.name || 'Cliente'}">
      <meta property="og:description" content="${(client.data?.summary || '').substring(0, 160)}...">
      <meta property="og:image" content="./preview.jpg">
      <meta property="og:type" content="website">
      <meta name="twitter:card" content="summary_large_image">
      <meta name="twitter:image" content="./preview.jpg">
          `;

          if (finalHtml.includes('</head>')) {
            finalHtml = finalHtml.replace('</head>', metaTags + '\n</head>');
          } else {
            // Fallback if no head tag
            finalHtml = metaTags + finalHtml;
          }
        }

        // Add index.html
        folder.file("index.html", finalHtml);
      }
    } catch (e) {
      console.error(`Erro ao processar o cliente para o ZIP (ID: ${client.id}, Nome: ${client.data?.name}):`, e);
    }
  });

  // Generate the zip blob
  const content = await zip.generateAsync({ type: "blob" });

  // Trigger download via DOM
  const url = window.URL.createObjectURL(content);
  const a = document.createElement("a");
  a.href = url;
  a.download = `ditho-export-${new Date().toISOString().split('T')[0]}.zip`;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  
  // Cleanup
  setTimeout(() => {
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }, 100);
};